import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { FLAC_SUPPORTED_MODELS, FLAC_UNSUPPORTED_MODELS } from '../../constants/flacCompatibility';

// Web shim has no `db`; combined with the 768px breakpoint this matches
// the `is-mobile-web` class App.js applies. On mobile we ship only the
// "Discover" surfaces (Mixpedia / Planner / For You / Crate Digger) —
// the desktop-only library tooling (Organize, Migrate, Compatibility,
// Loudness, etc.) isn't available on the mobile build, so it would
// just confuse readers to advertise it here.
const IS_MOBILE_WEB = typeof window !== 'undefined'
    && window.electron && !window.electron.db
    && window.matchMedia('(max-width: 768px)').matches;

const highlightText = (text, term) => {
    if (!term) return text;
    const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const parts = text.split(new RegExp(`(${escaped})`, 'gi'));
    return parts.map((part, i) =>
        part.toLowerCase() === term.toLowerCase()
            ? <mark key={i} className="about-search-highlight">{part}</mark>
            : part
    );
};

const processNode = (node, term) => {
    if (!term) return node;
    if (typeof node === 'string') return highlightText(node, term);
    if (Array.isArray(node)) return node.map((child, i) => processNode(child, term));
    if (!React.isValidElement(node)) return node;
    const children = React.Children.map(node.props.children, child => processNode(child, term));
    if (node.type === React.Fragment) {
        return <React.Fragment key={node.key}>{children}</React.Fragment>;
    }
    return React.cloneElement(node, {}, children);
};

const QUICK_LINKS_FULL = [
    { id: 'explore', label: 'Discover', isCategory: true },
    { id: 'mixpedia', label: 'Mixpedia' },
    { id: 'whats-next', label: 'Set Planner' },
    { id: 'feed', label: 'For You' },
    { id: 'crate-digger', label: 'Crate Digger' },
    { id: 'manage', label: 'Manage', isCategory: true },
    { id: 'library', label: 'Library' },
    { id: 'organize', label: 'Organize' },
    { id: 'metadata', label: 'Metadata' },
    { id: 'migrate', label: 'Migrate' },
    { id: 'prepare', label: 'Prepare', isCategory: true },
    { id: 'cues-loops', label: 'Cues & Loops' },
    { id: 'compatibility', label: 'Compatibility' },
    { id: 'loudness', label: 'Loudness' },
    { id: 'system', label: 'System Integration' },
    { id: 'specs', label: 'Technical Specs' },
];

const QUICK_LINKS_MOBILE = [
    { id: 'mixpedia', label: 'Mixpedia' },
    { id: 'whats-next', label: 'Set Planner' },
    { id: 'feed', label: 'For You' },
    { id: 'crate-digger', label: 'Crate Digger' },
];

const QUICK_LINKS = IS_MOBILE_WEB ? QUICK_LINKS_MOBILE : QUICK_LINKS_FULL;

const AboutTab = () => {
    const searchTerm = useSelector((state) => state.app.searchTerm);
    const contentRef = useRef(null);
    const matchIndexRef = useRef(0);
    const [appVersion, setAppVersion] = useState(null);

    // App version. Desktop resolves it over IPC (package.json via main.js);
    // web/native shims return the build-time REACT_APP_VERSION constant
    // (config-overrides.js). Hidden only if neither source is available.
    useEffect(() => {
        const getVersion = window.electron?.version?.get;
        if (!getVersion) return;
        let cancelled = false;
        Promise.resolve(getVersion())
            .then(v => { if (!cancelled && v) setAppVersion(v); })
            .catch(() => {});
        return () => { cancelled = true; };
    }, []);

    // Jump to the first match whenever the term changes.
    useEffect(() => {
        matchIndexRef.current = 0;
        if (!searchTerm || !contentRef.current) return;
        const marks = contentRef.current.querySelectorAll('mark.about-search-highlight');
        if (marks.length > 0) {
            marks[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [searchTerm]);

    // Cycle through matches on Enter / Shift+Enter (FindOverlay fires
    // 'search-next-match'). Without this the About page only ever landed on
    // the first match and never advanced — same gap Crate Digger had.
    useEffect(() => {
        const handleNextMatch = (e) => {
            if (!searchTerm || !contentRef.current) return;
            const marks = contentRef.current.querySelectorAll('mark.about-search-highlight');
            if (!marks.length) return;
            matchIndexRef.current = (matchIndexRef.current + e.detail + marks.length) % marks.length;
            marks[matchIndexRef.current]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        };
        window.addEventListener('search-next-match', handleNextMatch);
        return () => window.removeEventListener('search-next-match', handleNextMatch);
    }, [searchTerm]);

    const handleQuickLink = (id) => {
        const el = document.getElementById(`about-${id}`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const content = (
        <>
            <nav className="about-quick-links">
                {QUICK_LINKS.map(link => (
                    link.isCategory ? (
                        <span key={link.id} className="about-quick-link-category" onClick={() => handleQuickLink(link.id)}>
                            {link.label}
                        </span>
                    ) : (
                        <button key={link.id} className="about-quick-link" onClick={() => handleQuickLink(link.id)}>
                            {link.label}
                        </button>
                    )
                ))}
            </nav>

            {/* ═══ EXPLORE ═══ */}
            <div className="about-category-header" id="about-explore">
                <h3>Discover</h3>
                <p className="category-subtitle">Discover the Scene</p>
            </div>

            <div className="about-section" id="about-mixpedia">
                <h4>Mixpedia - See What DJs Play</h4>

                <p><strong>DJ Set Intelligence:</strong> Search any artist or track and discover what DJs are playing in their
                    sets worldwide. Mixpedia aggregates data from thousands of recorded DJ sets, festivals, and radio shows
                    to reveal the tracks that top DJs select and how they sequence them.</p>

                <p><strong>Key Capabilities:</strong></p>
                <ul>
                    <li><strong>Artist Search:</strong> Look up any DJ or producer to see their most-played tracks, frequent
                        collaborations, and genre patterns across sets.</li>
                    <li><strong>Track Search:</strong> Find any track and discover which DJs play it, how often it appears
                        in sets, and what tracks are commonly played alongside it.</li>
                    <li><strong>Co-Play Data:</strong> Understand track relationships—which songs are frequently played
                        together—to inform your own set building and crate digging.</li>
                </ul>

                <p><strong>Use Cases:</strong> Research what tracks are trending in your genre, discover new music through
                    the playlists of DJs you admire, and understand the sonic landscape of specific scenes or events.</p>
            </div>

            <div className="about-section" id="about-whats-next">
                <h4>Set Planner - Visual Set Builder</h4>

                <p><strong>Interactive Set Planning:</strong> Build a DJ set visually, track by track, using a node-based
                    canvas. Start with any track and explore branching recommendations to plan your set flow before you
                    hit the decks.</p>

                <p><strong>Key Capabilities:</strong></p>
                <ul>
                    <li><strong>Node-Based Canvas:</strong> Each track appears as a node on an interactive canvas. Select a
                        track to see recommended next tracks branch out visually, letting you explore different set directions.</li>
                    <li><strong>Set Path Tracking:</strong> Build a linear set path through the node graph. Undo selections,
                        break chains, and explore alternative branches without losing your work.</li>
                    <li><strong>Preview & Queue:</strong> Preview tracks directly from the canvas with the built-in audio player.
                        Export your finished set path to the player queue or save it as a playlist.</li>
                    <li><strong>Saved Sets:</strong> Save and load set plans for future sessions. Come back to refine your set
                        flow anytime.</li>
                </ul>

                <p><strong>Use Cases:</strong> Prepare sets for gigs by visually mapping track flow, discover unexpected
                    transitions through branching recommendations, and save set blueprints for recurring events or residencies.</p>
            </div>

            <div className="about-section" id="about-feed">
                <h4>For You - Personalized Feed</h4>

                <p><strong>Your Personal Discovery Stream:</strong> A personalized feed of track recommendations based on
                    the tracks you've liked and the artists you follow. Think of it as your own curated radio station, powered
                    by real DJ set data rather than generic algorithms.</p>

                <p><strong>Key Capabilities:</strong></p>
                <ul>
                    <li><strong>Smart Recommendations:</strong> Surfaces tracks that DJs play alongside your liked tracks,
                        as well as tracks played by artists you follow—giving you contextually relevant discoveries.</li>
                    <li><strong>Waveform Previews:</strong> Each track in the feed includes an interactive waveform for instant
                        preview. Click anywhere on the waveform to scrub, and playback syncs with the main player controls.</li>
                    <li><strong>Infinite Scroll:</strong> Keep scrolling to discover more tracks. The feed loads continuously
                        with fresh recommendations weighted by recency.</li>
                    <li><strong>Quick Actions:</strong> Like tracks, add them to playlists, or jump to their full Mixpedia
                        page directly from the feed.</li>
                </ul>

                <p><strong>Use Cases:</strong> Passive discovery of new music tailored to your taste, staying current with
                    what DJs in your scene are playing, and building playlists effortlessly from a curated stream.</p>
            </div>

            <div className="about-section" id="about-crate-digger">
                <h4>Crate Digger - Smart Track Discovery</h4>

                <p><strong>Data-Driven Recommendations:</strong> Get intelligent track recommendations based on co-play
                    analysis from real DJ sets. Unlike algorithmic suggestions based on audio similarity alone, MixGoat's
                    recommendations are derived from how professional DJs actually sequence and pair tracks.</p>

                <p><strong>Key Capabilities:</strong></p>
                <ul>
                    <li><strong>Playlist-Based Discovery:</strong> Select a playlist from your library and discover what
                        other tracks DJs commonly play alongside your tracks in their sets.</li>
                    <li><strong>Context-Aware Suggestions:</strong> Recommendations factor in real-world DJ decisions,
                        not just BPM or key matching, giving you musically relevant results that work on the dancefloor.</li>
                </ul>

                <p><strong>Use Cases:</strong> Fill gaps in your collection, discover tracks you might have missed, and
                    build cohesive set lists backed by real-world DJ data rather than guesswork.</p>
            </div>

            {/* Manage + Prepare describe desktop-only library tooling that isn't shipped
                on the mobile web build (Organize, Migrate, Compatibility, Loudness, etc).
                Render them on desktop only. */}
            {!IS_MOBILE_WEB && <>
            {/* ═══ MANAGE ═══ */}
            <div className="about-category-header" id="about-manage">
                <h3>Manage</h3>
                <p className="category-subtitle">Organize and Maintain Your Library</p>
            </div>

            <div className="about-section" id="about-library">
                <h4>Library - Advanced Analytics & Metadata Intelligence</h4>

                <p><strong>Professional Library Analysis:</strong> Comprehensive metadata aggregation and visualization system
                    that provides deep insights into your music collection's composition, helping inform curation decisions and
                    identify gaps or patterns in your library.</p>

                <p><strong>Analytics Capabilities:</strong></p>
                <ul>
                    <li><strong>Genre Distribution Analysis:</strong> Interactive pie chart visualization showing genre breakdown
                        with percentage calculations. Understand your collection's stylistic diversity or identify over/under-represented
                        genres in your library.</li>
                    <li><strong>Temporal Analysis:</strong> Bar chart displaying release year distribution. Identify era clusters
                        in your collection, track acquisition patterns over time, and spot historical gaps in your library.</li>
                    <li><strong>Attribute Statistics:</strong> Aggregate statistics for BPM ranges, loudness levels, key distribution,
                        and format types across your entire collection.</li>
                </ul>

                <p><strong>Metadata Display System:</strong></p>
                <ul>
                    <li><strong>Multi-Column Track View:</strong> Sortable, resizable columns displaying:
                        <ul>
                            <li>Artist & Title (from ID3/metadata tags)</li>
                            <li>Release Date (year/full date support)</li>
                            <li>Genre/Style (from Discogs/Beatport metadata)</li>
                            <li>Record Label</li>
                            <li>Musical Key (Camelot notation: C, C#, Dm, etc.)</li>
                            <li>BPM (beats per minute for mixing compatibility)</li>
                            <li>Integrated Loudness (LUFS measurement)</li>
                            <li>Pioneer Compatibility Status</li>
                        </ul>
                    </li>
                    <li><strong>Dynamic Column Resizing:</strong> Drag column boundaries to adjust widths for your workflow.
                        Prioritize metadata fields relevant to your mixing style.</li>
                    <li><strong>Real-time Search:</strong> Filter tracks by any text field across all metadata categories
                        simultaneously.</li>
                </ul>

                <p><strong>Advanced Filtering Engine:</strong></p>
                <ul>
                    <li><strong>Range-Based Filters:</strong>
                        <ul>
                            <li>BPM Range: Find tracks within specific tempo windows for harmonic mixing (e.g., 125-130 BPM for
                                tech house sets)</li>
                            <li>Loudness Range: Identify quiet tracks needing normalization or excessively loud tracks requiring
                                gain reduction</li>
                            <li>Release Date Range: Filter by era for throwback sets or recent releases</li>
                        </ul>
                    </li>
                    <li><strong>Musical Key Filtering:</strong> Multi-select key filter using Camelot wheel theory. Select
                        compatible keys for harmonic mixing (e.g., all tracks in 8A, 8B, 7A, 9A for seamless key-compatible sets)</li>
                    <li><strong>Compound Filtering:</strong> Combine multiple filters simultaneously (e.g., "125-130 BPM + D minor
                        key + released 2023-2024 + louder than -10 LUFS")</li>
                </ul>

                <p><strong>Professional Use Cases:</strong></p>
                <ul>
                    <li><strong>Set Preparation:</strong> Build track pools for specific gigs by filtering on BPM, energy level
                        (loudness), and key compatibility</li>
                    <li><strong>Collection Curation:</strong> Identify underutilized genres or eras in your library to inform
                        future purchases</li>
                    <li><strong>Quality Control:</strong> Spot tracks with compatibility issues, missing metadata, or loudness
                        outliers requiring attention</li>
                    <li><strong>Label Management:</strong> Track catalog numbers and release info for digital DJ pools or record
                        label submissions</li>
                </ul>
            </div>

            <div className="about-section" id="about-organize">
                <h4>Organize - Intelligent Library Management</h4>

                <p><strong>Automated File Organization:</strong> Restructure your music collection using metadata-driven folder
                    hierarchies and intelligent file naming conventions. Solve the chaos of inconsistent folder structures and
                    naming schemes that accumulate over years of music acquisition.</p>

                <p><strong>Revolutionary Metadata Retrieval:</strong> Unlike other tools that rely solely on embedded ID3 tags,
                    MixGoat is the first application to query a comprehensive music database containing tens of millions of tracks,
                    updated daily with new releases and catalog information. When you organize your library, the app cross-references
                    your files against this database to retrieve accurate, complete metadata including artist names, release dates,
                    labels, catalog numbers, and genre classifications - even for tracks with missing or incorrect embedded tags.
                    This database-driven approach ensures professional-quality organization that matches industry standards.</p>

                <p><strong>Organization Modes:</strong></p>
                <ul>
                    <li><strong>Folder Structure Organization:</strong> Create hierarchical folder systems based on metadata:
                        <ul>
                            <li>Artist &rarr; Year &rarr; Album</li>
                            <li>Genre &rarr; Artist &rarr; Year</li>
                            <li>Label &rarr; Artist &rarr; Release</li>
                            <li>Year &rarr; Genre &rarr; Artist</li>
                            <li>Custom combinations supporting up to three levels of hierarchy</li>
                        </ul>
                    </li>
                    <li><strong>File Renaming:</strong> Standardize filenames using metadata fields:
                        <ul>
                            <li>Append release dates: "Artist - Track [2024-03-15].wav"</li>
                            <li>Include catalog numbers: "Artist - Track [LABEL123].wav"</li>
                            <li>Prepend dates: "[2024] Artist - Track.wav"</li>
                            <li>Custom templates with multiple metadata fields</li>
                        </ul>
                    </li>
                </ul>

                <p><strong>Safety Features:</strong></p>
                <ul>
                    <li><strong>Preview Mode:</strong> Analyze mode shows the complete transformation before execution. Side-by-side
                        comparison displays current structure versus proposed structure with full file paths.</li>
                    <li><strong>Non-Destructive Operation:</strong> Files are moved, not copied, preserving disk space and
                        maintaining file metadata (creation dates, permissions).</li>
                    <li><strong>Collision Handling:</strong> Automatically resolves naming conflicts when multiple files would
                        result in identical names.</li>
                    <li><strong>Rollback Support:</strong> Tree view shows both original and target locations for manual
                        verification or reversal if needed.</li>
                </ul>

                <p><strong>Practical Applications:</strong> Ideal for DJs transitioning from disorganized downloads to professional
                    library management, consolidating multiple collection sources, or preparing tracks for digital distribution
                    or label submission with proper metadata standards.</p>
            </div>

            <div className="about-section" id="about-metadata">
                <h4>Metadata - Database-Driven Tag Correction</h4>

                <p><strong>Intelligent Metadata Repair:</strong> Automatically fetch and write accurate metadata to your audio files
                    using MixGoat's comprehensive music database. Unlike manual tag editing or tools that rely on acoustic fingerprinting,
                    Fix Metadata queries the same professional database used by the Organize feature—containing tens of millions of tracks
                    with daily updates—to retrieve and write complete, accurate tag information directly to your files.</p>

                <p><strong>The Problem:</strong> Downloaded tracks often have missing, incomplete, or incorrect metadata tags. This creates
                    issues when using DJ software, organizing libraries, or syncing to hardware players. Manually editing tags for hundreds
                    or thousands of tracks is time-consuming and error-prone, while acoustic fingerprinting services may fail on DJ edits,
                    remixes, or underground releases.</p>

                <p><strong>The Solution:</strong> MixGoat cross-references your audio files against its extensive database to retrieve
                    professional-grade metadata including artist names, track titles, record labels, release dates, genres, BPM, and
                    musical key. The system intelligently matches files using multiple data points to ensure accuracy, even when existing
                    tags are completely missing or incorrect.</p>

                <p><strong>Key Capabilities:</strong></p>
                <ul>
                    <li><strong>Database-Driven Matching:</strong> Queries the same database as the Organize feature—tens of millions
                        of tracks updated daily with new releases and catalog additions from labels, distributors, and music platforms.</li>
                    <li><strong>Before/After Comparison:</strong> Interactive comparison table shows current tags versus database metadata
                        side-by-side for every field, with visual highlighting of changed values.</li>
                    <li><strong>Selective Writing:</strong> Review all proposed changes before applying them. Select or deselect individual
                        files to control exactly which tracks get updated.</li>
                    <li><strong>Comprehensive Tag Coverage:</strong> Writes all standard metadata fields: Artist, Title, Album/Release,
                        Label, Release Date, Genre/Style, BPM, Musical Key, Catalog Number, and more.</li>
                    <li><strong>Smart Update Modes:</strong>
                        <ul>
                            <li><strong>Fill Empty Tags:</strong> Only writes to fields that are currently empty, preserving any existing
                                metadata you want to keep.</li>
                            <li><strong>Overwrite All:</strong> Replaces all tags with database values, ensuring complete consistency
                                with canonical metadata.</li>
                        </ul>
                    </li>
                    <li><strong>Backup Protection:</strong> Optional automatic backup of original files before writing tags. Backup files
                        are created with clear naming for easy recovery if needed.</li>
                    <li><strong>Batch Processing:</strong> Process entire folders with progress tracking, concurrent file operations,
                        and comprehensive error handling for locked files or unsupported formats.</li>
                </ul>

                <p><strong>Workflow:</strong></p>
                <ol>
                    <li><strong>Select Folder:</strong> Choose a folder containing audio files to analyze. Supports recursive scanning
                        of nested directories.</li>
                    <li><strong>Analyze:</strong> MixGoat scans files, reads current tags, queries the database, and generates a
                        comprehensive before/after comparison for every file with metadata matches.</li>
                    <li><strong>Review Changes:</strong> Examine the comparison table showing current versus new metadata. Changed
                        fields are visually highlighted. Select which files to update.</li>
                    <li><strong>Write Tags:</strong> Apply database metadata to selected files. Original files can be automatically
                        backed up before modification for safety.</li>
                </ol>

                <p><strong>Filename Normalization:</strong> Clean up messy filenames without touching metadata tags. The filename
                    editor provides two modes:</p>
                <ul>
                    <li><strong>Prettify:</strong> Automatically capitalizes words, strips leading track numbers, cleans up
                        separators, and standardizes formatting for consistent, readable filenames across your library.</li>
                    <li><strong>Smart Rename:</strong> Uses MixGoat's music database to correct artist names and track titles
                        in your filenames, fixing typos and formatting inconsistencies based on canonical metadata.</li>
                </ul>
                <p>Preview all changes side-by-side before applying, with inline editing for fine-tuning individual filenames.</p>

                <p><strong>Supported Formats:</strong> WAV, FLAC, MP3, AIFF/AIFC with full ID3v2.3/2.4, Vorbis Comments, and APEv2
                    tag support. Written tags are compatible with all major DJ software (Rekordbox, Serato, Traktor) and music players.</p>

                <p><strong>Use Cases:</strong> Essential for cleaning up purchased download collections from Bandcamp, Beatport, Juno,
                    or Traxsource where metadata quality varies significantly. Perfect for legacy vinyl rips or CD conversions that lack
                    proper tags. Invaluable when consolidating music from multiple sources into a unified, professionally-tagged library.</p>
            </div>

            <div className="about-section" id="about-migrate">
                <h4>Migrate - Cross-Platform DJ Library Migration</h4>

                <p><strong>Seamless Library Transfer:</strong> Migrate your entire DJ library between Rekordbox, Serato, Traktor,
                    and VirtualDJ with full preservation of tracks, playlists, cue points, and analysis data. MixGoat acts as a
                    universal translator between all four major DJ platforms, converting proprietary formats through a unified
                    intermediate model to ensure nothing is lost in translation.</p>

                <p><strong>The Problem:</strong> Switching between DJ software or using multiple platforms means rebuilding your
                    library from scratch—re-importing tracks, recreating playlists, and losing all your cue points, hot cues, loops,
                    and beat grids. Each platform stores data in incompatible proprietary formats (XML, NML, binary crates, SQLite databases),
                    making manual migration practically impossible for large libraries.</p>

                <p><strong>The Solution:</strong> MixGoat reads your source library, converts everything into a unified format, then
                    writes it out in your target platform's native format. The system handles all the complex format conversions—key
                    notation differences, BPM storage formats, rating scales, cue point colors, and file path encoding—automatically.</p>

                <p><strong>Supported Platforms:</strong></p>
                <ul>
                    <li><strong>Rekordbox:</strong> Reads from XML export files. Writes directly to the Rekordbox database for
                        instant library integration, with automatic XML fallback. Existing XML files are backed up before writing.</li>
                    <li><strong>Serato:</strong> Reads from the _Serato_ directory including database V2 and crate files. Writes
                        .crate files for direct library integration and embeds cue point data (Serato Markers2) into audio file
                        ID3 tags for full cue point transfer.</li>
                    <li><strong>Traktor:</strong> Reads and writes standard NML collection files. Compatible with Traktor Pro 2, 3, and 4.</li>
                    <li><strong>VirtualDJ:</strong> Reads and writes database.xml files. Merges into existing databases to preserve
                        VirtualDJ analysis data. Playlists are written as .vdjfolder files. Skips duplicate tracks automatically.</li>
                </ul>

                <p><strong>What Gets Migrated:</strong></p>
                <ul>
                    <li><strong>Track Metadata:</strong> Title, Artist, Album, Genre, Label, Comment, Year, Grouping, and Remixer</li>
                    <li><strong>Audio Analysis:</strong> BPM (normalized across platform-specific storage formats), Musical Key
                        (converted between Open Key, Camelot, standard notation, and Traktor numeric formats), and Rating
                        (normalized from each platform's unique scale to a standard 0-5 range)</li>
                    <li><strong>Cue Points:</strong> Hot cues (up to 8), memory cues, and loops—including position, color, and labels.
                        Colors are mapped across each platform's different color palette</li>
                    <li><strong>Beat Grids:</strong> Tempo markers with positions and BPM values for accurate beat synchronization</li>
                    <li><strong>Playlists:</strong> Full playlist hierarchies including folders and track ordering. When merging
                        from multiple sources, playlists are automatically prefixed with the source platform name to avoid conflicts</li>
                    <li><strong>File Properties:</strong> Duration, Bitrate, Sample Rate, and File Type information</li>
                </ul>

                <p><strong>Multi-Source Migration:</strong> Migrate from multiple source platforms simultaneously. MixGoat
                    deduplicates tracks by file path and intelligently merges playlists across sources, so you can consolidate
                    libraries from different platforms into a single unified collection.</p>

                <p><strong>Key Format Conversions:</strong></p>
                <ul>
                    <li><strong>Musical Keys:</strong> Automatically converts between standard notation (Am, C, F#), Camelot
                        notation (8A, 1B), Open Key notation (8d, 1m), and Traktor's numeric system (0-23)</li>
                    <li><strong>BPM:</strong> Handles differences in BPM storage—floating point values, integer-times-100,
                        and seconds-per-beat (period) formats</li>
                    <li><strong>Ratings:</strong> Normalizes between Rekordbox (0-255 stepped), Traktor (0-255 continuous),
                        and Serato/VirtualDJ (0-5) rating scales</li>
                    <li><strong>Cue Point Colors:</strong> Maps platform-specific color palettes to the closest matching colors
                        in the target platform</li>
                    <li><strong>File Paths:</strong> Handles URL-encoded paths, platform-specific path separators, and drive
                        letter formats for cross-platform compatibility</li>
                </ul>

                <p><strong>Workflow:</strong></p>
                <ol>
                    <li><strong>Select Sources:</strong> Choose one or more source platforms. MixGoat auto-detects installed
                        DJ software and pre-fills library paths. You can also manually browse for custom locations.</li>
                    <li><strong>Select Targets:</strong> Choose one or more target platforms to migrate to. You can migrate
                        to multiple platforms in a single operation.</li>
                    <li><strong>Analyze:</strong> Preview what will be migrated—total tracks, playlists, and cue points—without
                        modifying anything. Review the full source library contents including playlist structure and track listings.</li>
                    <li><strong>Migrate:</strong> Execute the migration with real-time progress tracking. Each track is processed
                        individually with success/error status reporting. Output files are created in platform-specific formats.</li>
                </ol>

                <p><strong>Safety Features:</strong></p>
                <ul>
                    <li><strong>Automatic Backups:</strong> Existing library files (Rekordbox XML, VirtualDJ database) are
                        automatically backed up before writing</li>
                    <li><strong>Non-Destructive:</strong> Source libraries are never modified—only read</li>
                    <li><strong>Cancellation Support:</strong> Stop migration mid-process without corrupting target libraries</li>
                    <li><strong>Duplicate Prevention:</strong> VirtualDJ writer skips tracks already present in the target database</li>
                    <li><strong>Error Isolation:</strong> Individual track failures don't stop the overall migration—errors are
                        logged and reported separately</li>
                </ul>

                <p><strong>Use Cases:</strong></p>
                <ul>
                    <li><strong>Platform Switching:</strong> Moving from Traktor to Rekordbox (or any combination) without losing
                        years of library organization and cue point work</li>
                    <li><strong>Multi-Platform DJs:</strong> Maintaining synchronized libraries across platforms you use for
                        different venues or purposes</li>
                    <li><strong>Library Consolidation:</strong> Merging collections from multiple DJ software installations
                        into a single platform</li>
                    <li><strong>Backup & Recovery:</strong> Export your library to other formats as a portable backup that can
                        be restored to any supported platform</li>
                </ul>
            </div>

            {/* ═══ PREPARE ═══ */}
            <div className="about-category-header" id="about-prepare">
                <h3>Prepare</h3>
                <p className="category-subtitle">Get Set Ready</p>
            </div>

            <div className="about-section" id="about-cues-loops">
                <h4>Cues & Loops - AI Cue Point & Loop Detection</h4>

                <p><strong>Concept:</strong> An AI-powered editor that automatically detects the structural cue
                    points of a track (intro, build, drop, break, outro) and seamless loop regions using the
                    on-device CUE-DETR model. Everything runs locally, so your audio never leaves your machine.
                    You review and refine the results in a frequency-colored waveform editor with gapless,
                    sample-accurate loop playback right inside the app.</p>

                <p><strong>Key Capabilities:</strong></p>
                <ul>
                    <li><strong>Automatic Detection:</strong> The CUE-DETR model analyzes each track and places
                        structural cue points and loop regions for you, so you start from a musically sensible
                        layout instead of a blank waveform.</li>
                    <li><strong>Cue Slots:</strong> 8 colored hot-cue slots for your key moments, plus unlimited
                        memory cues for everything else you want to mark.</li>
                    <li><strong>Loop Slots:</strong> Separate loop slots (A–H) with musical bar-length awareness
                        (8, 16, and 32 bars) for building clean, on-beat loops.</li>
                    <li><strong>Waveform Editor:</strong> A frequency-colored waveform lets you fine-tune positions
                        by ear and eye, with gapless, sample-accurate loop playback to audition loops in place.</li>
                    <li><strong>Undo/Redo:</strong> Full undo and redo (Cmd/Ctrl+Z) with edit coalescing so a run
                        of small adjustments collapses into sensible steps.</li>
                    <li><strong>Tunable Detection:</strong> Adjust sensitivity, minimum distance between cues,
                        maximum hot cues, snap-to-beat, and a loop focus mode (balanced, intro, drop, or groove)
                        to match how you play.</li>
                    <li><strong>Preserved Edits:</strong> Your manual changes are retained across re-detection, so
                        re-running the model never discards your hand-tuned cues and loops.</li>
                    <li><strong>Save & Export:</strong> Write cue and loop data back to your audio files, or export
                        to Rekordbox, Serato, Traktor, and VirtualDJ.</li>
                </ul>

                <p><strong>Use Cases:</strong> Ideal for DJs preparing large batches of tracks who want a fast,
                    consistent starting point for cues and loops without marking every track by hand, while keeping
                    full control to tweak and export into the DJ software they already use.</p>
            </div>

            <div className="about-section" id="about-compatibility">
                <h4>Compatibility - Audio File Compatibility Repair</h4>

                <p><strong>The Problem:</strong> Pioneer CDJ players frequently display error E-8305 ("Unsupported File Format")
                    when attempting to load certain WAV files, particularly those purchased from Bandcamp. This occurs because
                    Bandcamp encodes WAV files using "Wave Format Extensible" (WAVE_FORMAT_EXTENSIBLE) instead of standard PCM,
                    storing metadata that identifies the file as multi-channel audio even when it's stereo. While Rekordbox can
                    read these files, CDJ hardware firmware cannot process them, resulting in playback failures during live performances.</p>

                <p><strong>The Solution:</strong> MixGoat identifies and repairs problematic WAV files by analyzing the RIFF chunk
                    structure and converting incompatible format tags to standard PCM (wFormatTag value of 0x0001). The app also
                    handles AIFF to WAV conversion and can process various bit depths (16-bit, 24-bit, 32-bit).</p>

                <p><strong>File Classification System:</strong></p>
                <ul>
                    <li><strong>Compatible:</strong> Standard PCM WAV files that will load on all Pioneer hardware without issues</li>
                    <li><strong>Incompatible:</strong> Files using WAVE_FORMAT_EXTENSIBLE or other non-standard encodings that will
                        trigger E-8305 errors on CDJ players</li>
                    <li><strong>Depends:</strong> Files that may work on newer hardware (CDJ-3000, CDJ-2000NXS2) but fail on older
                        models (CDJ-2000NXS, CDJ-900). This includes FLAC files and high bit-depth formats.</li>
                </ul>

                <p><strong>FLAC Compatibility Reference:</strong></p>
                <ul>
                    <li><strong>Supported Models:</strong>{' '}
                        {FLAC_SUPPORTED_MODELS.map(({ model, spec }, i) => (
                            <React.Fragment key={model}>
                                {i > 0 && ', '}
                                {model}{spec && ` (${spec})`}
                            </React.Fragment>
                        ))}</li>
                    <li><strong>Unsupported Models:</strong> {FLAC_UNSUPPORTED_MODELS.join(', ')} — on these,
                        use the &ldquo;Convert FLAC files&rdquo; option to convert to a compatible format</li>
                </ul>

                <p><strong>Workflow:</strong></p>
                <ol>
                    <li><strong>Analyze:</strong> Scan your library to identify incompatible files. The app examines RIFF headers,
                        chunk structures, and format tags to determine compatibility status.</li>
                    <li><strong>Fix:</strong> Batch process problematic files. Fixed versions are created in the same directory
                        with original files automatically renamed with a [backup] suffix for safety.</li>
                    <li><strong>Implement:</strong> In Rekordbox, remove the original [backup] files from your library and import
                        the fixed versions. Re-analyze tracks if needed, then resync to USB for CDJ playback.</li>
                </ol>
            </div>

            <div className="about-section" id="about-loudness">
                <h4>Loudness - Professional Audio Normalization</h4>

                <p><strong>Technical Overview:</strong> Implements ITU-R BS.1770 loudness measurement standards to analyze and
                    normalize perceived loudness across your music collection. This prevents the common issue of volume inconsistency
                    between tracks during DJ sets, eliminating the need for constant gain adjustments.</p>

                <p><strong>Key Capabilities:</strong></p>
                <ul>
                    <li><strong>Target Loudness Configuration:</strong> Set your desired integrated loudness level in LUFS
                        (Loudness Units relative to Full Scale). Industry standards vary by context:
                        <ul>
                            <li>Club/Festival systems: -9 to -11 LUFS</li>
                            <li>Radio broadcast: -16 LUFS (EBU R128 standard)</li>
                            <li>Streaming platforms: -14 to -16 LUFS</li>
                            <li>Home listening: -18 to -20 LUFS</li>
                        </ul>
                    </li>
                    <li><strong>True Peak Limiting:</strong> Prevents inter-sample peaks and clipping during digital-to-analog
                        conversion by applying proper peak limiting before loudness adjustment.</li>
                    <li><strong>Dynamic Range Preservation:</strong> Uses gain adjustment rather than aggressive compression
                        to maintain the musical dynamics and character of your tracks.</li>
                    <li><strong>Batch Processing:</strong> Process entire folders with progress tracking and error handling
                        for corrupt or problematic files.</li>
                    <li><strong>Backup System:</strong> Optionally preserve original files before processing, with [backup]
                        suffix naming convention for easy identification.</li>
                </ul>

                <p><strong>Use Cases:</strong> Essential for DJs who mix tracks from different sources (vinyl rips, Beatport,
                    Bandcamp, Soundcloud) where mastering levels vary significantly. Creates consistent gain staging throughout
                    your set, reducing cognitive load and allowing focus on mixing rather than volume management.</p>
            </div>

            <div className="about-section" id="about-system">
                <h4>System Integration</h4>

                <p><strong>Integrated Music Player:</strong> Built-in audio engine with waveform display for track preview.
                    Play/pause controls, scrubbing, and queue management. Right-click tracks to add to player queue or create
                    playlists without leaving the analysis view.</p>

                <p><strong>Playlist Management:</strong> Create, edit, and manage playlists within the application. Save player
                    queue as reusable playlists. Drag tracks between playlists. Export playlists for use in Rekordbox or other
                    DJ software.</p>

                <p><strong>Drag & Drop Workflow:</strong> Universal drag-and-drop support across all tabs. Drop folders for
                    batch processing, individual files for quick analysis, or use system file browser for traditional selection.</p>

                <p><strong>Batch Processing Engine:</strong> Configurable concurrency levels (1-10 simultaneous operations) to
                    balance speed versus system resource usage. Progress tracking with real-time status updates. Pause/resume
                    capability for long-running operations.</p>

                <p><strong>Safety & Backup Systems:</strong> All destructive operations (fixing, normalizing, organizing) include
                    backup options with [backup] suffix convention. Preview modes for all operations show changes before execution.
                    Non-destructive analysis modes available for all features.</p>
            </div>

            <div className="about-section" id="about-specs">
                <h4>Technical Specifications</h4>

                <p><strong>Supported Audio Formats:</strong></p>
                <ul>
                    <li>WAV (PCM, WAVE_FORMAT_EXTENSIBLE, various bit depths)</li>
                    <li>AIFF/AIFC (with conversion to WAV)</li>
                    <li>FLAC (lossless compression)</li>
                    <li>MP3 (for metadata analysis only)</li>
                </ul>

                <p><strong>Pioneer Hardware Compatibility Matrix:</strong> Tested and verified with CDJ-3000, CDJ-2000NXS2,
                    CDJ-2000NXS, CDJ-900NXS, XDJ-1000MK2, XDJ-RX3. File compatibility analysis based on official Pioneer DJ
                    specifications and community-verified format support tables.</p>

                <p><strong>Metadata Standards:</strong> Supports ID3v2.3/2.4 tags, Vorbis comments, APEv2 tags. Reads from
                    Discogs, Beatport, and MusicBrainz metadata standards. Writes standards-compliant tags compatible with all
                    major DJ software platforms.</p>

                <p className="about-footer">MixGoat is designed for professional and aspiring DJs who demand reliable, professional-grade tools for music
                    library management. Whether you're preparing for club residencies, festival performances, radio shows, or
                    maintaining a personal collection of thousands of tracks, MixGoat provides the technical infrastructure needed
                    for confidence in your music library.</p>
            </div>
            </>}
            {appVersion && (
                <p style={{ textAlign: 'center', fontSize: '12px', color: 'var(--color-text-tertiary)', marginTop: '28px' }}>
                    MixGoat v{appVersion}
                </p>
            )}
        </>
    );

    return (
        <div className="about-tab" ref={contentRef}>
            {searchTerm ? processNode(content, searchTerm) : content}
        </div>
    );
};

export default AboutTab;
